/**
 * Keep a scroll area pinned to its bottom while its content grows — a reply
 * streaming in, an image finishing loading — unless the person has scrolled
 * up to read something, in which case leave them where they are.
 *
 * A ResizeObserver on the content rather than an effect per token: it catches
 * every cause of growth, including the ones React does not render (an `<img>`
 * decoding), and costs nothing when nothing changes. Callback refs, because
 * the scroll area mounts only once the chat has loaded.
 *
 * The scroll area itself is watched too, and the visual viewport: an
 * on-screen keyboard shrinks the view rather than the content, and the last
 * message should stay above it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** How close to the bottom still counts as "at the bottom". */
const SLACK = 80;

export function useStickToBottom() {
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const stuck = useRef(true);

  useEffect(() => {
    if (!scroller) return;
    // Only a scroll *upwards* unpins. The scroll event from our own
    // `scrollTop = scrollHeight` arrives a frame later, and if the content
    // grew in between — an image decoding, the next tokens — the distance from
    // the bottom reads as "the person scrolled away" and the chat stopped
    // following its own reply. Growth never moves scrollTop up; a person does.
    let last = scroller.scrollTop;
    const onScroll = () => {
      const top = scroller.scrollTop;
      if (scroller.scrollHeight - top - scroller.clientHeight < SLACK) stuck.current = true;
      else if (top < last - 2) stuck.current = false;
      last = top;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [scroller]);

  useEffect(() => {
    if (!scroller || !content) return;
    scroller.scrollTop = scroller.scrollHeight;
    // Not while words in it are selected: a reply can be quoted as it streams,
    // and moving the text would move it out from under the selection.
    const selecting = () => {
      const selection = window.getSelection();
      return !!selection && !selection.isCollapsed && !!selection.anchorNode && content.contains(selection.anchorNode);
    };
    const pin = () => {
      if (stuck.current && !selecting()) scroller.scrollTop = scroller.scrollHeight;
    };
    const observer = new ResizeObserver(pin);
    observer.observe(content);
    observer.observe(scroller);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', pin);
    return () => {
      observer.disconnect();
      viewport?.removeEventListener('resize', pin);
    };
  }, [scroller, content]);

  /** Pin again — after the person sends, they want to see their message. */
  const stick = useCallback(() => {
    stuck.current = true;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [scroller]);

  return { scrollRef: setScroller, contentRef: setContent, content, stick };
}
